import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface Plan {
  id: string;
  name: string;
  price: number;
  tokens: number;
  trialDays: number;
  features: string[];
  priceFormatted: string;
}

interface BillingStatus {
  hasActiveSubscription: boolean;
  planId: string;
  plan: Plan;
}

// Get shop domain from URL
const getShopDomain = () => new URLSearchParams(window.location.search).get('shop') || '';

export default function PlanSelectionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shopDomain = getShopDomain();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentPlan, setCurrentPlan] = useState<string>('free');
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Check for billing callback results
  useEffect(() => {
    const billingSuccess = searchParams.get('billing_success');
    const billingDeclined = searchParams.get('billing_declined');
    const billingError = searchParams.get('billing_error');
    const plan = searchParams.get('plan');

    if (billingSuccess === 'true' && plan) {
      setSuccessMessage(`Successfully subscribed to ${plan} plan!`);
      setCurrentPlan(plan);
    } else if (billingDeclined === 'true') {
      setError('Subscription was declined. You can try again or continue with the free plan.');
    } else if (billingError) {
      setError(`Billing error: ${billingError}`);
    }
  }, [searchParams]);

  // Fetch plans and current status
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch available plans
        const plansResponse = await fetch('/api/billing/plans');
        const plansData = await plansResponse.json();

        if (plansData.success) {
          setPlans(plansData.plans);
        }

        // Fetch current subscription status
        const statusResponse = await fetch(`/api/billing/status?shop=${shopDomain}`);
        const statusData = await statusResponse.json();

        if (statusData.success) {
          setCurrentPlan(statusData.planId || 'free');
        }
      } catch (err) {
        console.error('Error fetching billing data:', err);
        setError('Failed to load pricing plans');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [shopDomain]);

  const handleSelectPlan = async (planId: string) => {
    if (planId === currentPlan) {
      setError('You are already on this plan');
      return;
    }

    setSelectedPlan(planId);
    setIsSubscribing(true);
    setError('');

    try {
      const response = await fetch(`/api/billing/subscribe?shop=${shopDomain}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planId }),
      });

      const data = await response.json();

      if (data.success) {
        if (data.confirmationUrl) {
          // Redirect to Shopify billing approval page
          window.location.href = data.confirmationUrl;
        } else {
          // Free plan - no redirect needed
          setSuccessMessage('Plan updated successfully!');
          setCurrentPlan(planId);
        }
      } else {
        setError(data.error || 'Failed to subscribe');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError('Failed to process subscription');
    } finally {
      setIsSubscribing(false);
      setSelectedPlan('');
    }
  };

  const handleContinue = () => {
    navigate(`/onboarding?shop=${shopDomain}`);
  };

  // Styles
  const styles = {
    container: {
      padding: '24px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    header: {
      textAlign: 'center' as const,
      marginBottom: '32px',
    },
    title: {
      fontSize: '32px',
      fontWeight: 700,
      color: '#1a1a2e',
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '18px',
      color: '#666',
      marginBottom: '16px',
    },
    trialBadge: {
      display: 'inline-block',
      padding: '8px 16px',
      backgroundColor: '#28a745',
      color: 'white',
      borderRadius: '20px',
      fontSize: '14px',
      fontWeight: 600,
    },
    plansGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '24px',
      marginBottom: '32px',
    },
    planCard: (isSelected: boolean, isCurrent: boolean, isPopular: boolean) => ({
      padding: '24px',
      border: isPopular ? '2px solid #007bff' : isCurrent ? '2px solid #28a745' : '1px solid #e0e0e0',
      borderRadius: '16px',
      backgroundColor: isSelected ? '#f0f7ff' : isCurrent ? '#f8fff9' : 'white',
      boxShadow: isPopular ? '0 8px 24px rgba(0,123,255,0.15)' : '0 2px 8px rgba(0,0,0,0.05)',
      position: 'relative' as const,
      transition: 'all 0.2s',
    }),
    popularBadge: {
      position: 'absolute' as const,
      top: '-12px',
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '4px 16px',
      backgroundColor: '#007bff',
      color: 'white',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 600,
    },
    currentBadge: {
      position: 'absolute' as const,
      top: '-12px',
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '4px 16px',
      backgroundColor: '#28a745',
      color: 'white',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 600,
    },
    planName: {
      fontSize: '24px',
      fontWeight: 700,
      color: '#1a1a2e',
      marginBottom: '8px',
      marginTop: '8px',
    },
    planPrice: {
      fontSize: '36px',
      fontWeight: 700,
      color: '#007bff',
      marginBottom: '4px',
    },
    planPeriod: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '16px',
    },
    planTokens: {
      padding: '8px 12px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#333',
      marginBottom: '16px',
      textAlign: 'center' as const,
    },
    featureList: {
      listStyle: 'none',
      padding: 0,
      margin: '0 0 24px 0',
    },
    feature: {
      padding: '8px 0',
      fontSize: '14px',
      color: '#444',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    checkIcon: {
      color: '#28a745',
      fontWeight: 'bold',
    },
    button: (variant: 'primary' | 'secondary' | 'current' | 'loading') => ({
      width: '100%',
      padding: '14px 24px',
      fontSize: '16px',
      fontWeight: 600,
      border: 'none',
      borderRadius: '8px',
      cursor: variant === 'loading' || variant === 'current' ? 'not-allowed' : 'pointer',
      backgroundColor:
        variant === 'primary' ? '#007bff' :
        variant === 'current' ? '#6c757d' :
        variant === 'loading' ? '#ccc' :
        '#17a2b8',
      color: 'white',
      opacity: variant === 'loading' || variant === 'current' ? 0.7 : 1,
      transition: 'all 0.2s',
    }),
    errorBanner: {
      padding: '12px 16px',
      backgroundColor: '#f8d7da',
      border: '1px solid #f5c6cb',
      borderRadius: '8px',
      marginBottom: '24px',
      color: '#721c24',
      textAlign: 'center' as const,
    },
    successBanner: {
      padding: '12px 16px',
      backgroundColor: '#d4edda',
      border: '1px solid #c3e6cb',
      borderRadius: '8px',
      marginBottom: '24px',
      color: '#155724',
      textAlign: 'center' as const,
    },
    continueSection: {
      textAlign: 'center' as const,
      marginTop: '32px',
    },
    skipLink: {
      color: '#666',
      textDecoration: 'underline',
      cursor: 'pointer',
      fontSize: '14px',
    },
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <p>Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  // Determine popular plan
  const getPopularPlan = () => 'growth';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Choose Your Plan</h1>
        <p style={styles.subtitle}>Start with a 14-day free trial on any paid plan</p>
        <span style={styles.trialBadge}>14 Days Free Trial</span>
      </div>

      {/* Messages */}
      {error && <div style={styles.errorBanner}>{error}</div>}
      {successMessage && <div style={styles.successBanner}>{successMessage}</div>}

      {/* Plans Grid */}
      <div style={styles.plansGrid}>
        {plans.map((plan) => {
          const isPopular = plan.id === getPopularPlan();
          const isCurrent = plan.id === currentPlan;
          const isSelected = plan.id === selectedPlan;

          return (
            <div
              key={plan.id}
              style={styles.planCard(isSelected, isCurrent, isPopular)}
            >
              {isPopular && !isCurrent && (
                <span style={styles.popularBadge}>MOST POPULAR</span>
              )}
              {isCurrent && (
                <span style={styles.currentBadge}>CURRENT PLAN</span>
              )}

              <h3 style={styles.planName}>{plan.name}</h3>

              <div style={styles.planPrice}>
                {plan.price === 0 ? 'Free' : `$${plan.price}`}
              </div>
              <div style={styles.planPeriod}>
                {plan.price === 0 ? 'Forever' : 'per month'}
              </div>

              <div style={styles.planTokens}>
                <strong>{plan.tokens.toLocaleString()}</strong> tokens/month
              </div>

              <ul style={styles.featureList}>
                {plan.features.map((feature, index) => (
                  <li key={index} style={styles.feature}>
                    <span style={styles.checkIcon}>&#10003;</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan.id)}
                disabled={isCurrent || isSubscribing}
                style={styles.button(
                  isCurrent ? 'current' :
                  isSubscribing && isSelected ? 'loading' :
                  isPopular ? 'primary' :
                  'secondary'
                )}
              >
                {isCurrent
                  ? 'Current Plan'
                  : isSubscribing && isSelected
                    ? 'Processing...'
                    : plan.price === 0
                      ? 'Start Free'
                      : 'Start Free Trial'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Continue Section */}
      <div style={styles.continueSection}>
        <button
          onClick={handleContinue}
          style={{
            ...styles.button('primary'),
            width: 'auto',
            padding: '14px 48px',
          }}
        >
          Continue to Setup
        </button>
        <p style={{ marginTop: '16px' }}>
          <span
            style={styles.skipLink}
            onClick={handleContinue}
          >
            Skip for now (stay on free plan)
          </span>
        </p>
      </div>

      {/* FAQ Section */}
      <div style={{
        marginTop: '48px',
        padding: '24px',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Frequently Asked Questions</h3>

        <div style={{ marginBottom: '16px' }}>
          <strong>What are tokens?</strong>
          <p style={{ margin: '8px 0 0', color: '#666', fontSize: '14px' }}>
            Tokens are used for AI operations like Virtual Try-On sessions, PD measurements, and AI chat.
            Each operation has a token cost (e.g., VTO = 9 tokens, PD = 29 tokens, AI Chat = 35 tokens).
          </p>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <strong>What happens if I exceed my token limit?</strong>
          <p style={{ margin: '8px 0 0', color: '#666', fontSize: '14px' }}>
            You can purchase additional tokens at overage rates, or upgrade to a higher tier for better pricing.
          </p>
        </div>

        <div>
          <strong>Can I cancel anytime?</strong>
          <p style={{ margin: '8px 0 0', color: '#666', fontSize: '14px' }}>
            Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
          </p>
        </div>
      </div>
    </div>
  );
}
